import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons"
import cn from "../../utils/cn"
import { useVideoScanSummary } from "../../hooks/useVideoLogs"
import { formatISODate } from "../../utils/datetime"

// Shorter than the app-wide default, because two of these share one narrow row.
const scanTimeFormat = "MMM d, h:mm a"

const Stat = ({ label, value, hint }) => (
  <div className="rounded-lg bg-white px-2.5 py-2" title={hint}>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
      {label}
    </p>
    <p
      className={cn(
        "text-lg font-semibold leading-tight tabular-nums",
        value ? "text-gray-900" : "text-gray-400"
      )}
    >
      {value ?? 0}
    </p>
  </div>
)

const KeyValue = ({ label, value, emptyLabel = "—" }) => (
  <div className="flex justify-between gap-4 border-b border-gray-200 py-1 text-xs last:border-0">
    <span className="shrink-0 text-gray-500">{label}</span>
    <span
      className={cn(
        "text-right",
        value ? "font-medium text-gray-800" : "text-gray-400"
      )}
    >
      {value || emptyLabel}
    </span>
  </div>
)

/*
  When feedback was started and when it was last submitted, on one line.

  The repeat pill only appears past the first submission — one submission is
  already implied by the timestamp, so labelling it would add nothing.
*/
const FeedbackTimes = ({
  startedAt,
  startedCount,
  submittedAt,
  submittedCount,
}) => (
  <div
    className="flex justify-between gap-4 border-b border-gray-200 py-1 text-xs last:border-0"
    title={`${startedCount || 0} started, ${submittedCount || 0} submitted`}
  >
    <span className="shrink-0 text-gray-500">Feedback started → submitted</span>

    {startedAt || submittedAt ? (
      <span className="flex flex-wrap items-center justify-end gap-1.5 text-right">
        {submittedCount > 1 && (
          <span className="rounded-full border border-purple-200 bg-purple-50 px-1.5 text-[10px] text-purple-700">
            submitted {submittedCount} times
          </span>
        )}

        <span
          className={cn(
            startedAt ? "font-medium text-gray-800" : "text-gray-400"
          )}
        >
          {startedAt ? formatISODate(startedAt, scanTimeFormat) : "not started"}
        </span>
        <span className="text-gray-400">→</span>
        <span
          className={cn(
            submittedAt ? "font-medium text-gray-800" : "text-gray-400"
          )}
        >
          {submittedAt
            ? formatISODate(submittedAt, scanTimeFormat)
            : "not submitted"}
        </span>
      </span>
    ) : (
      <span className="text-gray-400">Not started</span>
    )}
  </div>
)

/*
  The latest of the individual responses' own timestamps.

  The summary's FeedbackSubmittedAt is the FIRST submission, which reads wrongly
  once several people have scanned the same QR code. Each response carries its
  own CreatedAt, so the most recent one is derived from those and only falls back
  to the summary field when the responses have not loaded.
*/
const latestSubmittedAt = (responses, fallback) => {
  const latest = responses.reduce(
    (newest, response) =>
      !newest || new Date(response.CreatedAt) > new Date(newest)
        ? response.CreatedAt
        : newest,
    null
  )
  return latest || fallback
}

/*
  QR-code scan engagement for one report.

  Scan figures exclude admin previews; previews are reported separately so a
  count that looks low can be told apart from one that was never scanned.
*/
const ScanEngagementCard = ({ uuid }) => {
  const { data, isLoading } = useVideoScanSummary(uuid)
  const summary = data?.summary
  const responses = data?.feedback || []

  return (
    <div className="rounded-xl bg-gray-50 p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          QR scan engagement
        </p>
        {!!summary?.PreviewOpens && (
          <span
            className="text-[10px] text-gray-500"
            title="Opens from this page's Preview button, excluded from the scan counts"
          >
            {summary.PreviewOpens} internal preview
            {summary.PreviewOpens === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((tile) => (
              <div
                key={tile}
                className="h-12 animate-pulse rounded-lg bg-gray-200"
              />
            ))}
          </div>
          <div className="h-16 animate-pulse rounded bg-gray-200" />
        </div>
      ) : summary ? (
        <div className="flex flex-col gap-2.5">
          <div className="grid grid-cols-3 gap-2">
            <Stat
              hint="Every page open from a QR scan"
              label="Total"
              value={summary.TotalScans}
            />
            <Stat
              hint="Distinct devices that scanned this report"
              label="Unique"
              value={summary.UniqueScans}
            />
            <Stat
              hint="Page opens from a mobile device"
              label="Mobile"
              value={summary.MobileScans}
            />
          </div>

          <div>
            <KeyValue
              emptyLabel="Never scanned"
              label="First → last scan"
              value={
                summary.FirstScanAt
                  ? `${formatISODate(summary.FirstScanAt, scanTimeFormat)} → ${
                      formatISODate(summary.LastScanAt, scanTimeFormat) || "—"
                    }`
                  : null
              }
            />
            {/* One line, but still two timestamps: the gap between starting and
                submitting is the only way to see feedback being abandoned, so
                they are compressed rather than merged. */}
            <FeedbackTimes
              startedAt={summary.FeedbackStartedAt}
              startedCount={summary.FeedbackStartedCount}
              submittedAt={latestSubmittedAt(
                responses,
                summary.FeedbackSubmittedAt
              )}
              submittedCount={summary.FeedbackSubmittedCount}
            />
          </div>

          {summary.UnattributedScans > 0 && (
            <div className="flex items-start gap-2 rounded border border-gray-200 bg-white p-2 text-[11px] text-gray-500">
              <FontAwesomeIcon
                className="mt-0.5 h-3 w-3 shrink-0"
                icon={faCircleInfo}
              />
              <span>
                {summary.UnattributedScans} scan
                {summary.UnattributedScans === 1 ? "" : "s"} came from a browser
                with storage blocked (private mode) and could not be attributed
                to a unique visitor — counted in the total only.
              </span>
            </div>
          )}
        </div>
      ) : (
        <p className="py-1 text-xs italic text-gray-400">
          This QR code has not been scanned yet.
        </p>
      )}
    </div>
  )
}

export default ScanEngagementCard
